import React from "react";
import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const COMPARE = [
  { label: "Incision size", endo: "Very small", conv: "Larger" },
  { label: "Muscle disruption", endo: "Minimal", conv: "More" },
  { label: "Blood loss", endo: "Reduced", conv: "Higher" },
  { label: "Typical recovery", endo: "Often faster", conv: "Longer" },
  { label: "Visualisation", endo: "Endoscopic HD", conv: "Direct/microscope" },
];

const BAR_FROM = { width: 0 };
const BAR_VIEWPORT = { once: true };
const BAR_TRANSITION = { duration: 1.1, ease: [0.16, 1, 0.3, 1] };

const Bar = ({ pct, tone }) => {
  const target = React.useMemo(() => ({ width: `${pct}%` }), [pct]);
  return (
    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <motion.div
        initial={BAR_FROM}
        whileInView={target}
        viewport={BAR_VIEWPORT}
        transition={BAR_TRANSITION}
        className={`h-full rounded-full ${tone}`}
      />
    </div>
  );
};

const WhatIsSection = () => {
  return (
    <section id="endoscopic" data-testid="what-is-section" className="relative bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
        {/* Left illustration */}
        <Reveal className="relative">
          <div className="relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-xl">
            <img
              src="https://images.pexels.com/photos/17483871/pexels-photo-17483871.png"
              alt="Neural network illustration representing spinal pathways"
              loading="lazy"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b2c4d]/80 via-[#0b2c4d]/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-xs uppercase tracking-[0.2em] text-white/80 mb-1">Keyhole precision</div>
              <div className="font-heading font-extrabold text-white text-xl">
                Treating the source, sparing the surroundings
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-brand/15 blur-2xl" />
        </Reveal>

        {/* Right explanation */}
        <div>
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">01 — The Procedure</p>
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
              What is Endoscopic Spine Surgery?
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Endoscopic spine surgery is a minimally invasive technique that uses a small endoscope
              and specialised instruments passed through a tiny incision. The goal is to address the
              source of nerve compression while preserving surrounding muscle and tissue.
            </p>
            <p className="mt-4 text-slate-500 leading-relaxed">
              It is not suitable for every patient. The most appropriate treatment always depends on
              your individual diagnosis, imaging and clinical evaluation — sometimes conventional or
              microscopic surgery is the better choice.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-9 glass-card rounded-2xl p-6">
            <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 items-center pb-3 border-b border-slate-200">
              <span className="text-xs uppercase tracking-wider text-slate-400">Comparison</span>
              <span className="text-xs font-semibold text-brand flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Endoscopic
              </span>
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1">
                <Minus className="w-3.5 h-3.5" /> Conventional
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {COMPARE.map((c, i) => (
                <div key={c.label} className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 items-center py-3">
                  <span className="text-sm text-slate-600">{c.label}</span>
                  <div>
                    <div className="text-sm text-[#0b2c4d] font-medium mb-1.5">{c.endo}</div>
                    <Bar pct={85 - i * 4} tone="bg-brand" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 mb-1.5">{c.conv}</div>
                    <Bar pct={55 - i * 3} tone="bg-slate-300" />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Illustrative comparison. Outcomes vary by individual and are determined after evaluation.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default WhatIsSection;
